import {
  useInsertProjectMembersMutation,
  useLeaveProjectMutation,
} from '@crew/apis/project/projectApis'
import { useCallback } from 'react'
import { useAppDispatch } from 'states/hooks'
import { ProjectMember, Project } from '@crew/models/domain'
import { NotifyEventType } from 'enums/app'
import {
  ObjectEventMessage,
  notifyProjectEvent,
  notifyProjectMemberEvent,
} from 'features/app/states/appSlice'
import { useGetLookupRolesQuery } from '@crew/apis/lookup/lookupApis'
import { RoleType } from '@crew/enums/domain'
import { Roles } from '@crew/enums/app'

export const useGroupChatHeadPanel = () => {
  const dispatch = useAppDispatch()

  // プロジェクトのロール情報を取得
  const { data: getRoles } = useGetLookupRolesQuery({
    roleType: RoleType.Project,
  })
  // プロジェクト退出API
  const [leaveProjectMutation, { isLoading: isLoadingLeaveGroupChat }] =
    useLeaveProjectMutation()
  // プロジェクトメンバー登録API
  const [
    insertProjectMembersMutation,
    { isLoading: isLoadingInsertGroupMember },
  ] = useInsertProjectMembersMutation()

  // メンバー登録処理
  const insertGroupMembers = useCallback(
    async (projectId: string, userIds: string[]) => {
      // 管理者ロールのIDを取得
      const adminRoleId = getRoles?.roles.find(
        (role) => role.roleCode === Roles.PrjAdmin.value
      )?.id
      if (!adminRoleId) {
        throw new Error('Admin role not found')
      }

      const payload = {
        projectId: projectId,
        users: userIds.map((user) => ({
          userId: user,
          roleId: adminRoleId,
        })),
      }

      await insertProjectMembersMutation({
        projectMembers: payload,
      }).unwrap()

      // メンバーが追加されたことを通知するイベントをトリガー
      const objectEventMessageGroupMember: ObjectEventMessage<ProjectMember> = {
        eventType: NotifyEventType.Inserted,
        id: projectId,
        object: undefined,
      }
      dispatch(notifyProjectMemberEvent(objectEventMessageGroupMember))

      // ヘッダーのメンバーリストを更新するイベントをトリガー
      const objectEventMessageProject: ObjectEventMessage<Project> = {
        eventType: NotifyEventType.Updated,
        id: projectId,
        object: {
          id: projectId,
        },
      }
      dispatch(notifyProjectEvent(objectEventMessageProject))
    },
    [dispatch, getRoles?.roles, insertProjectMembersMutation]
  )

  // グループから退出処理
  const leaveGroupChat = useCallback(
    async (projectId: string, userId: string) => {
      // プロジェクトから退出APIを実行する
      await leaveProjectMutation({
        projectId: projectId,
      }).unwrap()

      // メンバーリストを更新するイベントをトリガーする
      const objectEventMessageGroupMember: ObjectEventMessage<ProjectMember> = {
        eventType: NotifyEventType.Deleted,
        id: userId,
        object: {
          projectId,
          userId,
        },
      }
      dispatch(notifyProjectMemberEvent(objectEventMessageGroupMember))

      // グループチャットヘッダーのメンバーリストを更新するイベントをトリガーする
      const objectEventMessageProject: ObjectEventMessage<Project> = {
        eventType: NotifyEventType.Updated,
        id: projectId,
        object: {
          id: projectId,
        },
      }
      dispatch(notifyProjectEvent(objectEventMessageProject))
    },
    [leaveProjectMutation, dispatch]
  )

  return {
    leaveGroupChat,
    isLoadingLeaveGroupChat,
    insertGroupMembers,
    isLoadingInsertGroupMember,
  }
}
